import React, { useId } from 'react';

interface PersonIconProps {
  className?: string;
  size?: number;
}

/**
 * Person icon component
 * Displays two people figures for people/person entities
 */
export const PersonIcon: React.FC<PersonIconProps> = ({ 
  className = "", 
  size 
}) => {
  const sizeProps = size ? { width: size, height: size } : {};
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;
  
  return (
    <svg
      {...sizeProps}
      className={className}
      viewBox="0 0 512 512"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <defs>
        {/* Right person body gradient (blue) */}
        <linearGradient id={id("right-body")}>
          <stop offset="0" style={{ stopColor: '#00dddd', stopOpacity: 1 }} />
          <stop offset="0.53814715" style={{ stopColor: '#3b75c2', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#3032bf', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("right-body")}`}
          id={id("right-body-applied")}
          x1="33.591462"
          y1="361.33301"
          x2="302.3317"
          y2="361.33301"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(173.13001)"
        />
        
        {/* Left person body gradient (purple/pink) */}
        <linearGradient id={id("left-body")}>
          <stop offset="0" style={{ stopColor: '#cd49c5', stopOpacity: 1 }} />
          <stop offset="1" style={{ stopColor: '#7c2db9', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          xlinkHref={`#${id("left-body")}`}
          id={id("left-body-applied")}
          x1="207.35284"
          y1="362.03522"
          x2="476.09308"
          y2="362.03522"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(-173.13001,-0.86134336)"
        />
      </defs>
      <g>
        {/* Right person - shirt/collar */}
        <rect
          x="275.62985"
          y="180.02077"
          width="156.33383"
          height="56.417992"
          fill="#6f5d1b"
        />
        
        {/* Right person - neck */}
        <path
          d="m 311.37564,192.94092 h 73.21419 l 10,68.04613 h -91.21419 z"
          fill="#bca488"
        />
        
        {/* Right person - head */}
        <path
          d="m 422.67169,139.69098 c 1.13531,38.031 -38.49588,89.14904 -74.68896,89.14904 -31.02502,0 -74.68897,-51.11085 -74.68897,-89.14904 0,-49.235658 29.02111,-89.149044 74.68897,-89.149041 45.66785,3e-6 73.21954,39.926006 74.68896,89.149041 z"
          fill="#f8deb2"
        />
        
        {/* Right person - body */}
        <path
          d="m 295.44023,236.86914 c -49.14993,0 -88.71875,39.56882 -88.71875,88.71875 v 57.70899 13.7832 66.7168 h 88.71875 91.30273 88.71875 v -66.7168 -13.7832 -57.70899 c 0,-49.14993 -39.56882,-88.71875 -88.71875,-88.71875 l -45.65137,0 z"
          fill={`url(#${id("right-body-applied")})`}
        />
        
        {/* Left person - neck */}
        <path
          d="m 127.36379,184.71002 h 73.21419 l 10,68.04613 h -91.21419 z"
          fill="#7b663c"
        />
        
        {/* Left person - head */}
        <path
          d="m 238.65984,131.46008 c 1.13531,38.031 -38.49588,89.14904 -74.68896,89.14904 -31.02502,0 -74.688965,-51.11085 -74.688965,-89.14904 0,-49.235662 29.021105,-89.149042 74.688965,-89.149042 45.66785,0 73.21954,39.92601 74.68896,89.149042 z"
          fill="#a48447"
        />
        
        {/* Left person - face shading */}
        <path
          d="m 180.68211,45.493688 c 16.4763,16.33725 33.35237,41.495042 34.65999,69.687502 1.77588,38.2883 -10.06527,103.68471 -82.60725,89.14843 -2.18807,-0.43845 -4.53362,-0.29386 -6.875,-0.79882 12.18981,10.93336 25.55358,18.08007 37.15234,18.08007 36.19305,0 75.82476,-51.11942 74.68946,-89.15039 -1.26868,-42.498462 -21.99253,-78.041792 -57.01954,-86.966792 z"
          fill="#93773f"
        />
        
        {/* Left person - body */}
        <path
          d="m 122.94159,236.71001 c -49.149945,0 -88.718765,39.56882 -88.718765,88.71875 v 57.70899 13.7832 88.7168 h 88.718765 91.30273 88.71875 v -88.7168 -13.7832 -57.70899 c 0,-49.14993 -39.56882,-88.71875 -88.71875,-88.71875 z"
          fill={`url(#${id("left-body-applied")})`}
        />
        
        {/* Left person - hair */}
        <path
          d="M 164.08594 31.869141 A 81.39695 65.892769 0 0 0 82.689453 97.761719 A 81.39695 65.892769 0 0 0 89.564453 124.08984 A 81.39695 65.892769 0 0 1 164.93555 82.679688 A 81.39695 65.892769 0 0 1 239.49219 122.17383 A 81.39695 65.892769 0 0 0 245.48242 97.761719 A 81.39695 65.892769 0 0 0 164.08594 31.869141 z"
          fill="#555045"
        />
        
        {/* Right person - hair */}
        <path
          d="m 347.11514,47 c -45.38469,-7.751884 -81.39636,26.055857 -81.39649,62.44721 0.015,4.53273 -9.7285,81.39266 -9.86858,111.64434 -0.14008,30.25168 11.9073,16.47914 24.49567,14.16893 14.92169,-3.42313 12.87954,-135.661559 67.61901,-140.895306 65.06781,5.175294 60.74022,118.439916 73.69529,142.424676 1.94715,-3.87772 25.60856,25.91986 24.02743,-2.39257 C 444.10635,206.08484 432.78686,126.9838 428.51162,109.44721 417.7447,72.194517 390.34647,37.525433 347.11514,47 Z"
          fill="#9a8326"
        />
        
        {/* Right person - face highlight */}
        <path
          d="m 347.20427,93.967663 c -34.57501,3.305781 -46.47847,57.177847 -54.33008,97.357417 1.58857,2.15501 3.25548,4.24522 4.96094,6.28711 7.1855,-38.2725 23.57372,-94.80753 49.41797,-97.710936 32.74284,3.059976 45.17734,48.683336 44.74914,73.209156 -0.42821,24.52582 -20.77409,39.3396 -36.81565,45.43022 -6.38919,2.42583 -26.87901,-0.27287 -35.19912,-0.3425 9.25543,6.37349 18.72106,10.24414 27.23633,10.24414 23.0694,0 47.51781,-20.77401 61.93359,-45.6836 -7.59181,-39.63544 -21.0739,-85.539596 -61.95312,-88.791007 z"
          fill="#eccc99"
        />
        
        {/* Left person - ear */}
        <path
          d="m 162.61948,113.89454 21.31716,45.07057 -21.31716,6.09062 z"
          fill="#93773f"
        />
        
        {/* Right person - ear */}
        <path
          d="m 351.68484,138.71131 15.83561,34.10746 -15.83561,7.9178 z"
          fill="#eccc99"
        />
      </g>
    </svg>
  );
};
